import { ref, Ref } from '@vue/composition-api';
import tippy, { GetReferenceClientRect, Instance } from 'tippy.js';
import { UserSearchItem } from '@front-app/services/UserApi/UserModel';
import Mention, { MentionOptions } from '@tiptap/extension-mention';
import { mergeAttributes } from '@tiptap/vue-2';
import { isVue } from '@front-app/util/types';
import { v4 } from 'uuid';
import { getUsername } from '@front-app/util/user';
import {
	MentionSelectFunction,
	AtMentionConfig,
	AT_MENTION_TAG,
	AtMentionAttrs,
} from './AtMentionModel';

export const TrAtMention = Mention.extend({
	addAttributes() {
		return {
			id: {
				default: null,
				parseHTML: (element) => element.getAttribute('data-mention-id'),
				renderHTML: (attributes) => {
					if (!attributes.id) {
						return {};
					}

					return {
						'data-mention-id': attributes.id,
					};
				},
			},

			userId: {
				default: null,
				parseHTML: (element) => element.getAttribute('user-id'),
				renderHTML: (attributes) => {
					if (!attributes.userId) {
						return {};
					}

					return {
						'user-id': attributes.userId,
					};
				},
			},

			label: {
				default: null,
				// TODO: Strip leading '@' marks, but in a generic way so it works with
				// the config option customizing the leading mark
				parseHTML: (element) => element
					.textContent
					?.replace(new RegExp(`^${this.options.suggestion.char}`), ''),
				renderHTML() {
					return {};
				},
			},
		};
	},

	parseHTML() {
		return [
			{
				tag: AT_MENTION_TAG,
			},
		];
	},

	renderHTML({ node, HTMLAttributes }) {
		return [
			AT_MENTION_TAG,
			mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
			this.options.renderLabel({
				options: this.options,
				node,
			}),
		];
	},
});

export interface SuggestionConfig {
	config: MentionOptions['suggestion'];
	selectMention: MentionSelectFunction;
	mentionItems: Ref<UserSearchItem[]>;
}

type CommandFunction = (attrs: AtMentionAttrs) => void;

export const atMentionSuggestionConfig = (options: AtMentionConfig): SuggestionConfig => {
	const mentionItems: Ref<UserSearchItem[]> = ref([]);
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	let command: CommandFunction = () => {};
	const innerSelectFn: MentionSelectFunction = (user: UserSearchItem) => {
		command({
			id: v4(),
			label: getUsername(options.translationService, user),
			userId: user.id,
		});
	};

	const mainConfig: MentionOptions['suggestion'] = {

		decorationTag: AT_MENTION_TAG,

		items: options.mentionProvider,

		render: () => {
			let popup: Instance[];

			return {
				onStart: (props) => {
					if (!props.clientRect || !options.mentionListRef.value) {
						return;
					}

					command = props.command;
					mentionItems.value = (props.items as unknown as UserSearchItem[]);

					const el = isVue(options.mentionListRef.value) ? options.mentionListRef.value.$el : options.mentionListRef.value;

					popup = tippy('body', {
						// :(
						getReferenceClientRect: props.clientRect as GetReferenceClientRect,
						appendTo: () => document.body,
						sticky: true,
						content: el,
						showOnCreate: true,
						interactive: true,
						trigger: 'manual',
						placement: 'bottom-start',
						zIndex: 9999999999, // :(
					});
				},

				onUpdate(props) {
					command = props.command;
					mentionItems.value = (props.items as unknown as UserSearchItem[]);

					if (!props.clientRect) {
						return;
					}

					popup[0].setProps({
						// :(
						getReferenceClientRect: props.clientRect as GetReferenceClientRect,
					});
				},

				onKeyDown(props) {
					if (props.event.key === 'Escape') {
						popup[0].hide();

						return true;
					}

					// eslint-disable-next-line @typescript-eslint/no-explicit-any
					return (options.mentionListRef.value as any)?.onKeyDown(props);
				},

				onExit() {
					popup[0].destroy();
				},
			};
		},
	};

	const selectMention: MentionSelectFunction = (...args) => {
		innerSelectFn(...args);
	};

	return {
		config: mainConfig,
		selectMention,
		mentionItems,
	};
};
